import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import { ChevronLeft, Check } from 'lucide-react';
import { cn } from 'lib/utils';
import { DN } from '../../libs';
import { UserType } from '@app/models';
import { LayoutDefault } from '@app/layouts';
import {
  CompanyForm,
  UserForm,
  UserFormData,
  UserTypeForm,
} from './components';

interface StepperProps {
  title: string;
  steps: Array<{
    onClick: () => void;
    title: string;
  }>;
  currentStep: number;
  maxCurrentStep: number;
  onClose: () => void;
}

const Stepper: React.FC<StepperProps> = ({
  title,
  steps,
  currentStep,
  maxCurrentStep,
  onClose,
}) => {
  return (
    <div className="bg-white border-b border-gray-200 px-4 py-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={onClose} className="p-2">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-2xl font-semibold text-gray-900">{title}</h1>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {steps.map((step, index) => (
            <div key={index} className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Button
                  variant={currentStep === index ? 'default' : 'outline'}
                  size="sm"
                  className={cn(
                    'h-8 w-8 rounded-full p-0 text-sm',
                    maxCurrentStep >= index &&
                      currentStep !== index &&
                      'bg-green-100 border-green-300 text-green-700',
                    maxCurrentStep < index && 'opacity-50 cursor-not-allowed'
                  )}
                  onClick={step.onClick}
                  disabled={maxCurrentStep < index}
                >
                  {maxCurrentStep > index ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    index + 1
                  )}
                </Button>
                <span
                  className={cn(
                    'text-sm font-medium cursor-pointer',
                    currentStep === index ? 'text-gray-900' : 'text-gray-500',
                    maxCurrentStep < index && 'opacity-50'
                  )}
                  onClick={maxCurrentStep >= index ? step.onClick : undefined}
                >
                  {step.title}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div className="h-px w-8 bg-gray-300" />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const SignUpPage: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [maxCurrentStep, setMaxCurrentStep] = useState(0);
  const [dn, setDn] = useState<DN | null>(null);
  const [user, setUser] = useState<UserFormData>({} as UserFormData);
  const [userType, setUserType] = useState<UserType>(UserType.ENTITY);

  const onSubmitUserTypeForm = (type: UserType, dnData: DN | null) => {
    setUserType(type);
    setDn(dnData || null);
    setCurrentStep(1);
    setMaxCurrentStep((prev) => (prev <= 1 ? 1 : prev));
  };

  const onSubmitUserForm = (userData: UserFormData) => {
    setUser(userData);
    setCurrentStep(2);
    setMaxCurrentStep((prev) => (prev <= 2 ? 2 : prev));
  };

  const onClose = () => {
    navigate(-1);
  };

  const handleStepClick = (stepIndex: number) => {
    if (maxCurrentStep >= stepIndex) {
      setCurrentStep(stepIndex);
    }
  };

  const steps = [
    {
      onClick: () => handleStepClick(0),
      title: 'Тип пользователя',
    },
    {
      onClick: () => handleStepClick(1),
      title: 'Данные пользователя',
    },
    {
      onClick: () => handleStepClick(2),
      title: 'Организация',
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <LayoutDefault className="pt-0">
        <Stepper
          title="Регистрация"
          steps={steps}
          currentStep={currentStep}
          maxCurrentStep={maxCurrentStep}
          onClose={onClose}
        />

        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="space-y-4">
            {currentStep >= 0 && (
              <Card className={cn(currentStep !== 0 && 'opacity-75')}>
                <CardContent className="p-6">
                  <UserTypeForm
                    defaultExpanded={currentStep === 0}
                    onSubmit={onSubmitUserTypeForm}
                  />
                </CardContent>
              </Card>
            )}

            {currentStep >= 1 && (
              <Card className={cn(currentStep !== 1 && 'opacity-75')}>
                <CardContent className="p-6">
                  <UserForm
                    userType={userType}
                    dn={dn}
                    defaultExpanded={currentStep === 1}
                    onSubmit={onSubmitUserForm}
                  />
                </CardContent>
              </Card>
            )}

            {currentStep >= 2 && (
              <Card>
                <CardContent className="p-6">
                  <CompanyForm dn={dn as DN} user={user} />
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </LayoutDefault>
    </div>
  );
};

export default SignUpPage;
